<template>
    <div class="form" v-if="formPlaceholders">
        <vue-form-generator ref="vfg" :schema="schema" :model="model" :options="formOptions" tag="div"></vue-form-generator>
    </div>
</template>

<script>
    import VueFormGenerator from 'vue-form-generator';
    import dropdown from './vfg-field-dropdown';
    import 'vue-form-generator/dist/vfg.css';
    import { mapGetters } from 'vuex';

    VueFormGenerator.component.components.formGroup.components.fieldDropdown = dropdown;

    export default {

        components: {"vue-form-generator": VueFormGenerator.component},

        props: ['formModule', 'formModel', 'formSchema', 'formPlaceholders', 'formHints', 'formValidationErrorMessages', 'formSelectFieldOptions', 'formRadiosFieldOptions', 'formOverrides'],

        emits: ['isFormValid'],
        data () {
            return {
                model: this.formModel,
                schema: this.formSchema,
                formOptions: {
                    validateAfterChanged: false,
                    validateAsync: false
                },
            }
        },

        computed: {
            ...mapGetters('billing', [
                'billing'
            ]),
        },

        methods: {

            setFormLabels: function() {
                for (let group of this.schema.groups) {
                    for (let formInput of group.fields) {
                        // The .replace() inserts the billing email into the additional email label 
                        formInput.label = this.formPlaceholders[formInput.model].replace('__billingEmail__', this.billing.email);
                    }
                }
            },

            setFormHints: function() {
                for (let group of this.schema.groups) {
                    for (let formInput of group.fields) {
                        if (formInput.model in this.formHints) {
                            formInput.hint = this.formHints[formInput.model];
                        }
                    }
                }
            },

            setFieldValues: function(fieldType, fieldToOptions) {
                for (let group of this.schema.groups) {
                    for (let formInput of group.fields) {
                        if (formInput.type === fieldType) {
                            // set values if passed in from props
                            if (Object.keys(fieldToOptions).length > 0) {
                                formInput.values = fieldToOptions[formInput.model];
                            }

                            // set default if no prepopulated value
                            if (this.model[formInput.model]) {
                                // Look if a field was given a pre-populated value (from salesforce)
                                this.model[formInput.model] = this.model[formInput.model]
                            } else if (formInput.default) {
                                // Look if the default attribute on the field object is populated. Use that value as default
                                this.model[formInput.model] = formInput.default;
                            } else if (typeof formInput.values === "function") {
                                // Look to see if values attribute is a function. If it is, call it and set default to first value from returned array (it should always return an array)
                                let values = formInput.values(this.model);
                                this.model[formInput.model] = values.length > 0 ? values[0].value : "";
                            } else if (formInput.values.length > 0) {
                                // Look to see if values attribute is a populated array. If it is, set default to first value from array
                                this.model[formInput.model] = formInput.values[0].value
                            } else {
                                // Set default to empty string
                                this.model[formInput.model] = ""
                            }
                        }
                    }
                }
            },

            setFieldValidationMessages: function() {
                for (let group of this.schema.groups) {
                    for (let formInput of group.fields) {
                        formInput.validator.forEach((validation, index, validators) => {
                            //validation type stored in 'validationType' prop for custom validations
                            //but stored in 'name' for built-in. Can't use name for custom because its a
                            //reserved Javascript function object property
                            let validationType = validation.validationType || validation.name;
                            if (validationType in this.formValidationErrorMessages) {
                                let errorMessages = this.formValidationErrorMessages[validationType];
                                validators[index] = validation.locale(errorMessages);
                            }
                        })
                    }
                }
            },

            overrideFormFieldAttributeValues: function() {
                for (let group of this.schema.groups) {
                    for (let formInput of group.fields) {
                        for (let overrideAttr in this.formOverrides) {
                            formInput[overrideAttr] = this.formOverrides[overrideAttr];
                        }
                    }
                }
            },

            validateField: function(updatedFormField) {
                for (let formField of this.$refs.vfg.$children) {
                    if (updatedFormField === formField.field.model) {
                        formField.validate();
                    }
                }
            },


            buildWatchEvents: function() {
                for (let formInput in this.model) {
                    this.$watch(`model.${formInput}`, function(updatedValue) {
                        this.$store.commit(`${this.formModule}/set${(formInput.charAt(0).toUpperCase() + formInput.slice(1))}`, updatedValue);
                        this.validateField(formInput);
                        this.$emit('isFormValid',this.$refs.vfg.validate())
                    });
                }
            },
        },

        mounted() {
            this.buildWatchEvents();
            this.setFormLabels();
            this.setFormHints();
            this.setFieldValues("dropdown", this.formSelectFieldOptions);
            this.setFieldValues("radios", this.formRadiosFieldOptions);
            this.setFieldValidationMessages();
            this.overrideFormFieldAttributeValues();
            this.$emit('isFormValid',this.$refs.vfg.validate())
        }
    };
</script>

<style lang="scss" scoped>

    .form {
        width: 100%;
        height: 100%;
        font-family: "Nitti Grotesk", sans-serif;
        font-style: normal;
        font-weight: normal;
        margin: 0 auto;
    }

    ::v-deep {

        .vue-form-generator {
            position: relative;
            label {
                font-weight: bolder;
                font-size: 15px;
                margin: 0.5em auto;
                color: #00263E;
            }

            .error {
                background-color: white;
                text-align: left;
                margin: 0px;
                padding: 0px 11px;
            }

            .errors {
                text-align: left;

                span {
                    background-image: none;
                    padding-left: 0px;
                }
            }

            .form-control {
                position: relative;
                width: 100%;
                height: 44px;
                font-size: 18px;
                color:#302f32;
                margin-bottom: 1.5rem;
                line-height: 2.0rem;
                text-align: left;
                background: #FFFFFF;
                border: 1px solid #B2BFC7;
                box-sizing: border-box;
                box-shadow: none;
                transition: none;
                border-radius: 4px;
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                outline: none;
            }

            .field-wrap {
                width: 100%;
                padding: 0px;
            }

            .additional-email {
                display: flex;
                flex-direction: row-reverse;
                justify-content: flex-end;
                height: 30px;
                margin-top: -20px;
                display: none;
            }

            .additional-email .field-wrap {
                height: 15px;
                width: 15px;
                margin-top: 3px;
                margin-right: 5px;
            }

            .additional-email .form-control {
                margin: 0;
                height: 100% !important;
                width: 100% !important;
                margin-right: 3px;
                min-width: 15px;
            }

            .additional-email label {
                margin: 0;
                font-weight: 300 !important;
            }

            #additional-email {
                margin-top: 1px;
                margin-left: 0px;
            }

            @media only screen and (max-width: 736px) {
                .additional-email {
                    margin-top: 10px;
                    margin-bottom: 30px;
                }
            }

            .shipping-method {
                label {
                    font-size: 24px;
                    font-weight: normal;
                    margin-bottom: 24px;
                }

                .radio-list {
                    label {
                        font-weight: bold;
                        font-size: 18px;
                        margin-bottom: 24px;
                    }
                }
            }

            /* only display on tablet and above */
            @media only screen and (min-width: 736px) {
                .col.xs-12 {
                    max-width: 96%; /* Safari display fix */
                }

                .col.md-6 {
                    max-width: 48%; /* Safari display fix */
                }

                .col.md-3 {
                    max-width: 24%; /* Safari display fix */
                }
            }
        }
    }
</style>
